package com.iregained.eventapp.pages

import androidx.compose.runtime.*
import com.iregained.eventapp.components.models.EventDetailsData
import com.iregained.eventapp.components.models.PatientIntakeFormState
import com.iregained.eventapp.components.models.TimeSlotData
import com.iregained.eventapp.components.sections.event_booking.sections.EventConfirmationSection
import com.iregained.eventapp.components.sections.event_booking.sections.EventHeroSection
import com.iregained.eventapp.components.sections.event_booking.sections.EventIntakeFormSection
import com.iregained.eventapp.components.sections.event_booking.sections.EventSlotPickerSection
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.utils.LocalBreakpoint
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

@Page("events/{slug}")
@Composable
fun EventBookingPage() {
    val ctx = rememberPageContext()
    val slug = ctx.route.params["slug"] ?: ""
    val palette = ColorMode.current.toSitePalette()
    val coroutineScope = rememberCoroutineScope()

    // Data States
    var eventData by remember { mutableStateOf<EventDetailsData?>(null) }
    var availableSlots by remember { mutableStateOf<List<TimeSlotData>>(emptyList()) }
    var loadingError by remember { mutableStateOf("") }

    // UI States
    var selectedSlot by remember { mutableStateOf<TimeSlotData?>(null) }
    val formState = remember { PatientIntakeFormState() }
    var isSubmitting by remember { mutableStateOf(false) }
    var isSubmitted by remember { mutableStateOf(false) }
    var generatedToken by remember { mutableStateOf("") }

    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.XL

    // 1. Fetch Live Event & Slot Data
    LaunchedEffect(slug) {
        if (slug.isNotEmpty()) {
            document.documentElement?.classList?.add("preloader-active")

            try {
                val db = Firebase.firestore

                // Fetch Event Document matching the URL slug
                val eventsQuery = db.collection("events").where { "slug" equalTo slug }.get()

                if (eventsQuery.documents.isNotEmpty()) {
                    val eventDoc = eventsQuery.documents.first()
                    val loadedEvent = eventDoc.data<EventDetailsData>()
                    eventData = loadedEvent

                    // Fetch Time Slots for this specific event
                    val slotsQuery = db.collection("events/${loadedEvent.eventId}/time_slots").get()
                    val fetchedSlots = slotsQuery.documents.map { it.data<TimeSlotData>() }

                    // --- NEW: If Firestore has no slots, dynamically build them from the schedule ---
                    availableSlots = fetchedSlots.ifEmpty { generateDynamicSlots(loadedEvent) }

                } else {
                    loadingError = "Event not found or is no longer active."
                }
            } catch (e: Exception) {
                console.error("Firestore Error:", e)
                loadingError = "Failed to load event details. Please check your connection."
            } finally {
                document.documentElement?.classList?.remove("preloader-active")
            }
        }
    }

    // Minimal Standalone Wrapper
    Column(
        modifier = Modifier.fillMaxSize().backgroundColor(palette.custom.surfaceBackground).minHeight(100.vh),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Subtle Top Marketing Bar
        Row(
            modifier = Modifier.fillMaxWidth().padding(leftRight = 5.percent, topBottom = 15.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Link(path = "/") {
                Image(
                    src = Res.Images.Common.LOGO,
                    modifier = Modifier.userSelect(UserSelect.None).height(40.px).cursor(Cursor.Pointer)
                )
            }
        }

        // Constrained Main Content Area
        Column(
            modifier = Modifier.fillMaxWidth().maxWidth(900.px).padding(leftRight = 5.percent, topBottom = 24.px)
                .styleModifier { property("margin", "0 auto") }, horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Handle Loading/Error States
            if (loadingError.isNotEmpty()) {
                SpanText(loadingError, modifier = Modifier.padding(40.px).color(palette.custom.brandPrimary))
            } else if (eventData != null) {

                // Render the Event UI
                EventHeroSection(eventData = eventData!!)

                if (!isSubmitted) {
                    EventSlotPickerSection(
                        availableSlots = availableSlots,
                        selectedSlot = selectedSlot,
                        onSlotSelected = { selectedSlot = it })

                    if (selectedSlot != null) {
                        EventIntakeFormSection(
                            formState = formState, isSubmitting = isSubmitting, onSubmit = {
                                isSubmitting = true
                                document.documentElement?.classList?.add("preloader-active")

                                // 2. Write Booking Data to Firestore
                                coroutineScope.launch {
                                    try {
                                        val newToken = "tok_" + (100000..999999).random()
                                        val db = Firebase.firestore

                                        db.collection("events/${eventData!!.eventId}/patients").add(
                                            mapOf(
                                                "eventId" to eventData!!.eventId,
                                                "fullName" to formState.fullName,
                                                "email" to formState.email,
                                                "phone" to formState.phone,
                                                "conditionNotes" to formState.conditionNotes,
                                                "consentTransactional" to formState.consentTransactional,
                                                "consentMarketing" to formState.consentMarketing,
                                                "consentSms" to formState.consentSms,
                                                "slotId" to selectedSlot!!.slotId,
                                                "accessToken" to newToken,
                                                "status" to "BOOKED"
                                            )
                                        )

                                        generatedToken = newToken
                                        isSubmitted = true
                                    } catch (e: Exception) {
                                        console.error("Failed to submit booking:", e)
                                    } finally {
                                        isSubmitting = false
                                        document.documentElement?.classList?.remove("preloader-active")
                                    }
                                }
                            })
                    }
                } else {
                    EventConfirmationSection(
                        patientName = formState.fullName,
                        accessToken = generatedToken,
                        slotTime = "${selectedSlot?.date} at ${selectedSlot?.startTime}"
                    )
                }
            }
        }

        // Minimal Footer
        Box(
            modifier = Modifier.fillMaxWidth().padding(topBottom = 40.px), contentAlignment = Alignment.Center
        ) {
            SpanText(
                text = "© 2026 IRegained Inc. Secure Booking System.",
                modifier = Modifier.fontSize(12.px).color(palette.custom.darkText)
            )
        }
    }
}

// --- NEW: Helper function to generate slots from the operating schedule ---
fun generateDynamicSlots(event: EventDetailsData): List<TimeSlotData> {
    val generatedSlots = mutableListOf<TimeSlotData>()

    event.operatingSchedule.filter { !it.isClosed }.forEach { day ->
        val startParts = day.startTime.split(":")
        val endParts = day.endTime.split(":")

        if (startParts.size == 2 && endParts.size == 2) {
            var currentMins = (startParts[0].toIntOrNull() ?: 0) * 60 + (startParts[1].toIntOrNull() ?: 0)
            val endMins = (endParts[0].toIntOrNull() ?: 0) * 60 + (endParts[1].toIntOrNull() ?: 0)

            // The length of the actual appointment (e.g., 60 mins)
            val duration = event.defaultSlotDurationMinutes.takeIf { it > 0 } ?: 60

            // How often a new slot becomes available (e.g., every 30 mins)
            val intervalStep = 30

            // As long as the FULL duration fits before closing time
            while (currentMins + duration <= endMins) {
                val slotStartStr = "${(currentMins / 60).toString().padStart(2, '0')}:${
                    (currentMins % 60).toString().padStart(2, '0')
                }"

                // Calculate when this specific slot ends
                val endOfSlotMins = currentMins + duration
                val slotEndStr = "${(endOfSlotMins / 60).toString().padStart(2, '0')}:${
                    (endOfSlotMins % 60).toString().padStart(2, '0')
                }"

                generatedSlots.add(
                    TimeSlotData(
                        slotId = "${day.date}_${slotStartStr.replace(":", "")}",
                        date = day.date,
                        startTime = slotStartStr,
                        endTime = slotEndStr,
                        capacity = event.defaultSlotCapacity,
                        bookedCount = 0,
                        isBlocked = false
                    )
                )

                // Move forward by the INTERVAL (30 mins), rather than the full duration
                currentMins += intervalStep
            }
        }
    }
    return generatedSlots
}